import * as React from 'react';
import { Helmet } from 'react-helmet';
import { Redirect } from 'react-router-dom';

import { POOL_CLUSTER, POOL_TYPE } from 'defs/pools';
import routes, { getBaseTitle, getSeoDataFromViewId } from 'defs/routes';

import Sequences from 'defs/sequences';
import i18n from 'helper/i18n';

import { Loader } from 'components/atoms/Loader';
import withSequenceState from 'components/hoc/withSequenceState';
import { WithSequenceStatePropsType } from 'components/hoc/withSequenceState';
import { CoachIsEnumType } from 'components/molecules/CoachSelector';
import Pool from 'components/organisms/Pool';
import { renderPoolItemsForType } from 'components/views/PoolView/render/items';
import { RenderPoolItemsAdditionalProps } from 'components/views/PoolView/render/items';
import CustomWorkout from 'containers/molecules/Pool/CustomWorkout';
import Empty from 'containers/molecules/Pool/Empty';
import { PoolClusterType, PoolEnumType } from 'types/services/magnolia/enums/PoolEnum';
import withVirtualPageView, { WithVirtualPageView } from '../../hoc/withVirtualPageView';

export type PoolViewOwnPropsType = {
	selectedPool: PoolEnumType;
	selectedCluster: PoolClusterType;
};

export type PoolViewContainerProps = {
	loaded: boolean;
	valid: boolean;
	items: any[];
	total: number;
	errors: Error[];
	weight: number;
	favoriteIds: string[];
	coach: CoachIsEnumType | null;
	ready: boolean;
	signature: string;
};

export type PoolViewContainerDispatchProps = {
	doLoadFavorites: () => void;
	doRequestPool: (selectedPool: PoolEnumType, programItemExternalId?: string) => void;
	doUnsetCurrentOverlayRef: () => void;
};

export type PoolViewPropsType = PoolViewOwnPropsType &
	PoolViewContainerProps &
	PoolViewContainerDispatchProps &
	WithSequenceStatePropsType &
	WithVirtualPageView;

export class PoolView extends React.Component<PoolViewPropsType> {
	public static displayName = 'PoolView';

	public static defaultProps: PoolViewOwnPropsType & Partial<PoolViewContainerProps> = {
		valid: false,
		loaded: false,
		items: [],
		errors: [],
		weight: 0,
		favoriteIds: [],
		coach: 'MIA',
		ready: false,
		selectedCluster: POOL_CLUSTER.ALL,
		selectedPool: undefined as any,
		signature: '',
		total: -1,
	};

	public shouldComponentUpdate(nextProps: PoolViewContainerProps & PoolViewContainerDispatchProps) {
		// don't update if items.length is still 0, but only if pool has not loaded yet
		if (this.props.items.length === 0 && nextProps.items.length === 0 && nextProps.ready === false) {
			return false;
		}

		// update if signature changes
		if (this.props.signature !== nextProps.signature) {
			return true;
		}

		// update if view was not ready before
		if (!this.props.ready && nextProps.ready) {
			return true;
		}

		// update if validity changes
		if (nextProps.valid !== this.props.valid) {
			return true;
		}

		return false;
	}

	public componentDidUpdate(prevProps) {
		// (Re)Load Favorites when accessing the Favorites Tab
		if (
			this.props.selectedCluster === POOL_CLUSTER.FAVORITE &&
			prevProps.selectedCluster !== this.props.selectedCluster
		) {
			this.props.doLoadFavorites();
		}

		if (prevProps.selectedCluster !== this.props.selectedCluster) {
			this.googleAnalyticsTrackPageView();
		}
	}

	public componentDidMount() {
		if (this.props.valid) {
			this.props.doRequestPool(this.props.selectedPool);
		}

		if (this.props.selectedCluster === POOL_CLUSTER.FAVORITE) {
			this.props.doLoadFavorites();
		}

		this.googleAnalyticsTrackPageView();
	}

	public googleAnalyticsTrackPageView() {
		// false because we want to trigger it when changeing the filter tabs (all -> favorites)
		this.props.trackVirtualPageView({}, 1, false);
	}

	public render() {
		const { ready, coach, weight, valid, items, selectedCluster, selectedPool, total } = this.props;

		if (!valid) {
			return <Redirect to={`${routes.today.indexRoute.href}?redirectedBy=pool~${selectedPool}`} />;
		}

		const seo = getSeoDataFromViewId(routes.pool.indexRoute.id, {
			selectedPool: i18n(`react.impulsCoach.routes.pool.selectedPool.${selectedPool}.title`),
		});

		return (
			<React.Fragment>
				<Helmet>
					<title>{`${seo.title} ${getBaseTitle()}`}</title>
					<meta name="description" content={seo.meta.description} />
					{seo.meta.description && <meta name="description" content={`${seo.meta.description}`} />}
					{seo.og.title && <meta property="og:title" content={`${seo.og.title}`} />}
				</Helmet>

				{selectedCluster !== POOL_CLUSTER.CUSTOM && (
					<Pool
						title={i18n(`react.impulsCoach.views.pools.${selectedPool}.title`)}
						modifier={selectedPool}
						selectedCluster={selectedCluster}
						selectedPool={selectedPool}
					>
						{!ready && <Loader />}

						{ready &&
							items.length > 0 &&
							renderPoolItemsForType(selectedPool, items, {
								weight,
							} as RenderPoolItemsAdditionalProps)}

						{ready && items.length === 0 && (
							<Empty
								total={total}
								coach={coach}
								selectedPool={selectedPool}
								selectedCluster={selectedCluster}
							/>
						)}
					</Pool>
				)}

				{selectedCluster === POOL_CLUSTER.CUSTOM && selectedPool === POOL_TYPE.WORKOUT && (
					<Pool
						title={i18n(`react.impulsCoach.views.pools.${selectedPool}.title`)}
						modifier={selectedPool}
						selectedCluster={selectedCluster}
						selectedPool={selectedPool}
						hideFilters
					>
						<CustomWorkout />
					</Pool>
				)}
			</React.Fragment>
		);
	}
}

export default withVirtualPageView(withSequenceState(PoolView, [Sequences.POOL_IS_LOADING]));
